import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CheckCircle2, XCircle, Undo2, Loader2, MapPin, Wrench, Camera, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { ClearableInput } from "../components/common/ClearableInput";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import MyWorkActivity from "../components/MyWorkActivity";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function useGeo() {
  return () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ location_status: "unavailable", geo: null });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ location_status: "captured", geo: { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy } }),
      () => resolve({ location_status: "denied", geo: null }), { timeout: 5000 });
  });
}

function List({ status, actions, refreshKey, materialTypeId, selectable, bulkActions }) {
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(false); const [search, setSearch] = useState("");
  const [serialFrom, setSerialFrom] = useState(""); const [serialTo, setSerialTo] = useState(""); const [sort, setSort] = useState("serial_asc");
  const [sel, setSel] = useState(new Set());
  const dSearch = useDebouncedValue(search);
  const dSerialFrom = useDebouncedValue(serialFrom);
  const dSerialTo = useDebouncedValue(serialTo);
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    setLoading(true);
    const reqId = ++loadSeq.current;   // latest-wins: stale responses never overwrite newer ones
    try { const { data } = await api.get("/field/materials", { params: { current_status: status, search: dSearch, material_type_id: materialTypeId || undefined, serial_from: dSerialFrom || undefined, serial_to: dSerialTo || undefined, sort, page_size: 50 } }); if (reqId === loadSeq.current) setItems(data.items || []); }
    finally { if (reqId === loadSeq.current) setLoading(false); }
  }, [status, dSearch, materialTypeId, dSerialFrom, dSerialTo, sort]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setSel(new Set()); }, [refreshKey, status]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = items.length > 0 && sel.size === items.length;
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(items.map((m) => m.id)));
  const clear = () => setSel(new Set());
  return (
    <div className="space-y-3">
      <ClearableInput value={search} onChange={setSearch} placeholder="Search serial..." testid={`field-search-${status}`} />
      <div className="flex gap-2">
        <ClearableInput value={serialFrom} onChange={setSerialFrom} placeholder="Serial from..." className="text-sm" testid={`field-from-${status}`} />
        <ClearableInput value={serialTo} onChange={setSerialTo} placeholder="Serial to..." className="text-sm" testid={`field-to-${status}`} />
      </div>
      <div className="flex items-center gap-2">
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-9 text-xs" data-testid={`field-sort-${status}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="serial_asc">Serial A → Z</SelectItem>
            <SelectItem value="serial_desc">Serial Z → A</SelectItem>
            <SelectItem value="recent">Most recent</SelectItem>
          </SelectContent>
        </Select>
        {(serialFrom || serialTo) && <Button variant="ghost" size="sm" data-testid={`field-range-clear-${status}`} onClick={() => { setSerialFrom(""); setSerialTo(""); }}>Clear range</Button>}
      </div>
      {selectable && items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-600"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid={`field-select-all-${status}`} /> Select all ({items.length})</label>
          {sel.size > 0 && <><span className="text-xs font-semibold text-teal-700" data-testid={`field-sel-count-${status}`}>{sel.size} selected</span>{bulkActions && bulkActions([...sel], clear)}</>}
        </div>
      )}
      {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
        : items.length === 0 ? <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">Nothing here</div>
        : items.map((m) => (
          <div key={m.id} className={`rounded-xl border bg-white p-4 shadow-sm ${sel.has(m.id) ? "border-teal-400 ring-1 ring-teal-400" : "border-slate-200"}`} data-testid={`field-card-${m.id}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-mono text-sm font-semibold text-slate-900">
                {selectable && <Checkbox checked={sel.has(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`field-select-${m.id}`} />}
                {m.material_number}
              </span>
              <Badge variant="secondary">{m.type_name}</Badge>
            </div>
            <div className="mt-1 text-xs text-slate-500">{m.project_name}</div>
            <div className="mt-3 flex gap-2">{actions(m)}</div>
          </div>
        ))}
    </div>
  );
}

export default function FieldWorkspace() {
  const { fieldTab, setFieldTab } = useWorkspace();
  const [dash, setDash] = useState({ counts: {}, by_type: {} });
  const [refresh, setRefresh] = useState(0);
  const [rejOpen, setRejOpen] = useState(false); const [retOpen, setRetOpen] = useState(false);
  const [active, setActive] = useState(null); const [reason, setReason] = useState("");
  const [bulkRej, setBulkRej] = useState(null);       // {ids, clear} when rejecting many at once
  const [idemKey, setIdemKey] = useState("");
  const [busyId, setBusyId] = useState(null);        // card with an in-flight action (double-tap guard)
  const [submitting, setSubmitting] = useState(false); // dialog confirm in-flight
  const getGeo = useGeo();
  const [selType, setSelType] = useState(""); const [types, setTypes] = useState([]);
  useEffect(() => { api.get("/material-types", { params: { page_size: 500 } }).then((r) => setTypes(r.data.items || [])).catch(() => {}); }, []);

  const loadDash = () => api.get("/field/dashboard").then((r) => setDash(r.data)).catch(() => {});
  useEffect(() => { loadDash(); }, [refresh]);
  const bump = () => { setRefresh((n) => n + 1); };

  const geoLabel = (s) => (s === "captured" ? "location captured" : s === "denied" ? "location permission denied" : "location unavailable");
  const errMsg = (e) => e.response?.data?.detail || (e.response ? "Failed" : "Network issue — please retry when back online");

  const acknowledge = async (m) => {
    if (busyId) return;
    setBusyId(m.id);
    const loc = await getGeo();
    try {
      const { data } = await api.post("/field/acknowledge", { material_ids: [m.id], idempotency_key: uuidv4(), ...loc });
      toast.success(`Acknowledged — ${geoLabel(data.location_status)}`); bump();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };
  const acknowledgeMany = async (ids, clear) => {
    if (submitting || !ids.length) return;
    setSubmitting(true);
    const loc = await getGeo();
    try {
      const { data } = await api.post("/field/acknowledge", { material_ids: ids, idempotency_key: uuidv4(), ...loc });
      toast.success(`Accepted ${data.success}${data.failed ? `, ${data.failed} failed` : ""} — ${geoLabel(data.location_status)}`);
      clear && clear(); bump();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };
  const openReject = (m) => { setActive(m); setBulkRej(null); setReason(""); setIdemKey(uuidv4()); setRejOpen(true); };
  const openBulkReject = (ids, clear) => { setActive(null); setBulkRej({ ids, clear }); setReason(""); setIdemKey(uuidv4()); setRejOpen(true); };
  const openReturn = (m) => { setActive(m); setReason(""); setIdemKey(uuidv4()); setRetOpen(true); };

  const doReject = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      if (bulkRej) {
        const { data } = await api.post("/field/reject-bulk", { material_ids: bulkRej.ids, reason, idempotency_key: idemKey });
        toast.success(`Rejected ${data.success}${data.failed ? `, ${data.failed} failed` : ""} — returned to store`);
        bulkRej.clear && bulkRej.clear();
      } else {
        await api.post("/field/reject", { material_id: active.id, reason, idempotency_key: idemKey });
        toast.success("Rejected — returned to store");
      }
      setRejOpen(false); setReason(""); setBulkRej(null); bump(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };
  const doReturn = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    if (submitting) return;
    setSubmitting(true);
    const loc = await getGeo();
    try { await api.post("/field/return-request", { material_id: active.id, reason, geo: loc.geo, idempotency_key: idemKey });
      toast.success(`Return requested — ${geoLabel(loc.location_status)}`); setRetOpen(false); setReason(""); bump(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };
  const [instOpen, setInstOpen] = useState(false); const [instMat, setInstMat] = useState(null);
  const [consumer, setConsumer] = useState(""); const [instDate, setInstDate] = useState(new Date().toISOString().slice(0, 10));
  const [atts, setAtts] = useState([]); const [instKey, setInstKey] = useState(""); const [installing, setInstalling] = useState(false);
  const fileRef = useRef();

  const openInstall = (m) => { setInstMat(m); setConsumer(""); setInstDate(new Date().toISOString().slice(0, 10)); setAtts([]); setInstKey(uuidv4()); setInstOpen(true); };
  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setAtts((prev) => {
      const room = 3 - prev.length;
      if (picked.length > room) toast.error("Maximum 3 attachments");
      return [...prev, ...picked.slice(0, room).map((f) => ({ file: f, url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null, isImage: f.type.startsWith("image/"), name: f.name }))];
    });
    e.target.value = "";
  };
  const removeAtt = (i) => setAtts((prev) => prev.filter((_, idx) => idx !== i));
  const doInstall = async () => {
    if (!consumer.trim()) { toast.error("Consumer Number required"); return; }
    if (!instDate) { toast.error("Installation date required"); return; }
    if (installing) return;
    setInstalling(true);
    const loc = await getGeo();
    try {
      const fd = new FormData();
      fd.append("material_id", instMat.id); fd.append("consumer_number", consumer.trim());
      fd.append("actual_installation_date", instDate); fd.append("location_status", loc.location_status);
      if (loc.geo) { fd.append("geo_lat", loc.geo.lat); fd.append("geo_lng", loc.geo.lng); if (loc.geo.accuracy != null) fd.append("geo_accuracy", loc.geo.accuracy); }
      fd.append("idempotency_key", instKey);
      atts.forEach((a) => fd.append("files", a.file));
      const { data } = await api.post("/field/install", fd);
      toast.success(`Installed — ${data.attachment_count} attachment(s), ${geoLabel(loc.location_status)}`); setInstOpen(false); bump();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setInstalling(false); }
  };
  const selTypeName = types.find((t) => t.id === selType)?.type_name;
  const c = selType ? (dash.by_type?.[selTypeName] || { pending_ack: 0, acknowledged: 0, return_requested: 0, installed: 0, completed_data_entry: 0 }) : (dash.counts || {});
  const CARDS = [
    ["Assigned", c.pending_ack, "text-blue-600", "pending"],
    ["Acknowledged", c.acknowledged, "text-emerald-600", "ack"],
    ["Installed", c.installed, "text-sky-600", "installed"],
    ["Return Requested", c.return_requested, "text-amber-600", "return"],
    ["Completed (Data Entry)", c.completed_data_entry, "text-teal-600", "completed"],
  ];

  return (
    <div>
      <PageHeader title="Field Workspace" subtitle="Your assigned materials" />
      <MyWorkActivity />
      <div className="mb-4" data-testid="field-type-filter-wrap">
        <Label className="text-xs text-slate-500">Material Type</Label>
        <Select value={selType || "__all__"} onValueChange={(v) => setSelType(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="field-type-filter" className="mt-1 h-10 max-w-xs"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.type_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="field-dashboard">
        {CARDS.map(([l, v, cls, tab]) => (
          <button key={l} type="button" data-testid={`field-kpi-${tab}`} onClick={() => setFieldTab(tab)}
            className={`rounded-xl border bg-white p-3 text-center shadow-sm transition-colors ${fieldTab === tab ? "border-teal-500 ring-1 ring-teal-500" : "border-slate-200 hover:border-teal-300"}`}>
            <div className={`font-display text-xl font-bold ${cls}`}>{v ?? 0}</div>
            <div className="mt-1 text-[11px] leading-tight text-slate-500">{l}</div>
          </button>
        ))}
      </div>
      <Tabs value={fieldTab} onValueChange={setFieldTab}>
        <TabsList data-testid="field-tabs" className="grid h-auto w-full grid-cols-5 gap-1 p-1">
          <TabsTrigger value="pending" data-testid="field-tab-pending" className="whitespace-normal px-0.5 py-1.5 text-[11px] leading-tight">Assigned</TabsTrigger>
          <TabsTrigger value="ack" data-testid="field-tab-ack" className="whitespace-normal px-0.5 py-1.5 text-[11px] leading-tight">Ack</TabsTrigger>
          <TabsTrigger value="installed" data-testid="field-tab-installed" className="whitespace-normal px-0.5 py-1.5 text-[11px] leading-tight">Installed</TabsTrigger>
          <TabsTrigger value="return" data-testid="field-tab-return" className="whitespace-normal px-0.5 py-1.5 text-[11px] leading-tight">Return</TabsTrigger>
          <TabsTrigger value="completed" data-testid="field-tab-completed" className="whitespace-normal px-0.5 py-1.5 text-[11px] leading-tight">Completed</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-4">
          <List status="ASSIGNED_TO_FIELD_USER" materialTypeId={selType} refreshKey={refresh}
            selectable bulkActions={(ids, clear) => (<>
              <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" disabled={submitting} onClick={() => acknowledgeMany(ids, clear)} data-testid="field-bulk-accept">
                {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Accept selected</Button>
              <Button size="sm" className="h-7 bg-red-600 hover:bg-red-700" disabled={submitting} onClick={() => openBulkReject(ids, clear)} data-testid="field-bulk-reject">
                <XCircle className="mr-1 h-4 w-4" /> Reject selected</Button>
            </>)}
            actions={(m) => (<>
            <Button data-testid={`ack-btn-${m.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busyId === m.id} onClick={() => acknowledge(m)}>
              {busyId === m.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Accept</Button>
            <Button data-testid={`reject-btn-${m.id}`} size="sm" variant="outline" disabled={!!busyId} onClick={() => openReject(m)}><XCircle className="mr-1 h-4 w-4 text-red-500" /> Reject</Button>
          </>)} />
        </TabsContent>
        <TabsContent value="ack" className="mt-4">
          <List status="ACKNOWLEDGED_BY_FIELD_USER" materialTypeId={selType} refreshKey={refresh} actions={(m) => (<>
            <Button data-testid={`install-btn-${m.id}`} size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => openInstall(m)}><Wrench className="mr-1 h-4 w-4" /> Install</Button>
            <Button data-testid={`return-btn-${m.id}`} size="sm" variant="outline" onClick={() => openReturn(m)}><Undo2 className="mr-1 h-4 w-4 text-amber-600" /> Return</Button>
          </>)} />
        </TabsContent>
        <TabsContent value="return" className="mt-4">
          <List status="RETURN_REQUESTED" materialTypeId={selType} refreshKey={refresh} actions={() => <Badge className="bg-amber-100 text-amber-700">Awaiting store</Badge>} />
        </TabsContent>
        <TabsContent value="installed" className="mt-4">
          <List status="INSTALLED_BY_FIELD_USER" materialTypeId={selType} refreshKey={refresh} actions={(m) => (
            <div className="text-right text-xs text-slate-500">
              <div className="font-medium text-indigo-700">Consumer {m.consumer_number || "—"}</div>
              <div>{m.actual_installation_date || ""}{m.install_attachment_count ? ` · ${m.install_attachment_count} file(s)` : ""}</div>
            </div>
          )} />
        </TabsContent>
        <TabsContent value="completed" className="mt-4">
          <List status="COMPLETED_DATA_ENTRY" materialTypeId={selType} refreshKey={refresh} actions={(m) => (
            <div className="text-right text-xs text-slate-500">
              <div className="font-medium text-teal-700">Consumer {m.consumer_number || "—"}</div>
              <div>Data entry completed</div>
            </div>
          )} />
        </TabsContent>
      </Tabs>

      <Dialog open={rejOpen} onOpenChange={(o) => !submitting && setRejOpen(o)}><DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">{bulkRej ? `Reject ${bulkRej.ids.length} selected material(s)` : `Reject · ${active?.material_number}`}</DialogTitle></DialogHeader>
        <div><Label>Reason *</Label><Input data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" /></div>
        <DialogFooter><Button variant="outline" disabled={submitting} onClick={() => setRejOpen(false)}>Cancel</Button>
          <Button data-testid="reject-confirm-btn" disabled={submitting} onClick={doReject} className="bg-red-600 hover:bg-red-700">{submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Reject</Button></DialogFooter>
      </DialogContent></Dialog>
      <Dialog open={retOpen} onOpenChange={(o) => !submitting && setRetOpen(o)}><DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Request Return · {active?.material_number}</DialogTitle></DialogHeader>
        <div><Label>Reason *</Label><Input data-testid="return-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" /></div>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" /> Your location will be captured if permission is granted.</p>
        <DialogFooter><Button variant="outline" disabled={submitting} onClick={() => setRetOpen(false)}>Cancel</Button>
          <Button data-testid="return-confirm-btn" disabled={submitting} onClick={doReturn} className="bg-amber-600 hover:bg-amber-700">{submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Request Return</Button></DialogFooter>
      </DialogContent></Dialog>

      <Dialog open={instOpen} onOpenChange={(o) => !installing && setInstOpen(o)}><DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Install · {instMat?.material_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Consumer Number *</Label><Input data-testid="install-consumer-input" value={consumer} onChange={(e) => setConsumer(e.target.value)} className="mt-1" placeholder="Enter consumer number" /></div>
          <div><Label>Actual Installation Date *</Label><Input data-testid="install-date-input" type="date" max={new Date().toISOString().slice(0, 10)} value={instDate} onChange={(e) => setInstDate(e.target.value)} className="mt-1" /></div>
          <div>
            <Label>Attachments <span className="text-xs font-normal text-slate-400">(optional, up to 3 · ≤500KB each)</span></Label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" multiple onChange={addFiles} className="hidden" data-testid="install-file-input" />
            <div className="mt-2 grid grid-cols-3 gap-2">
              {atts.map((a, i) => (
                <div key={i} className="relative rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid={`install-att-${i}`}>
                  {a.isImage ? <img src={a.url} alt="" className="h-16 w-full rounded object-cover" /> : <div className="flex h-16 flex-col items-center justify-center text-slate-500"><FileText className="h-5 w-5" /><span className="mt-0.5 max-w-full truncate text-[9px]">{a.name}</span></div>}
                  <button type="button" onClick={() => removeAtt(i)} data-testid={`install-att-remove-${i}`} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white"><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              {atts.length < 3 && <button type="button" data-testid="install-add-file" onClick={() => fileRef.current?.click()} className="flex h-[72px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-400 hover:border-indigo-400 hover:text-indigo-500"><Camera className="h-5 w-5" /><span className="mt-0.5 text-[10px]">Add</span></button>}
            </div>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" /> Your location will be captured if permission is granted.</p>
        </div>
        <DialogFooter><Button variant="outline" disabled={installing} onClick={() => setInstOpen(false)}>Cancel</Button>
          <Button data-testid="install-confirm-btn" disabled={installing} onClick={doInstall} className="bg-indigo-600 hover:bg-indigo-700">{installing && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Confirm Install</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
